import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Modal } from "@perlab/ui";
import { SUBAPPS } from "@perlab/registry";
import { useShellStore } from "../state/shellStore";
import { buildShellCommands, filterCommands, type ShellCommand } from "./commands";

import { useEntitlements } from "../../lib/auth/EntitlementsContext";
/**
 * Shell Command Palette + Quick-Open. Always mounted; registers the global
 * ⌘K / Ctrl+K hotkey (toggles via the shellStore contract) and renders the
 * overlay when open. Jumps to any shell nav target, app, or scope home — real
 * navigation, no stubs. Scope is read from the live route params.
 */
export function ShellCommandPalette(): React.ReactElement | null {
  const open = useShellStore((s) => s.commandPaletteOpen);
  const toggle = useShellStore((s) => s.toggleCommandPalette);
  const close = useShellStore((s) => s.closeCommandPalette);
  const navigate = useNavigate();
  const { ws, client, eng, project } = useParams({ strict: false }) as {
    ws?: string;
    client?: string;
    eng?: string;
    project?: string;
  };
  const { hasAppAccess } = useEntitlements();

  const [query, setQuery] = useState("");
  const [sel, setSel] = useState(0);

  // Global ⌘K / Ctrl+K toggle.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggle]);

  const commands = useMemo(
    () => buildShellCommands({ ws, client, eng, project }, SUBAPPS.filter((app) => hasAppAccess(app.id))),
    [ws, client, eng, project, hasAppAccess],
  );
  const filtered = useMemo(() => filterCommands(commands, query), [commands, query]);

  // Reset selection when the query or open-state changes.
  useEffect(() => {
    setSel(0);
  }, [query, open]);

  if (!open) return null;

  const dismiss = () => {
    close();
    setQuery("");
  };
  const run = (c: ShellCommand | undefined) => {
    if (!c) return;
    navigate(c.target as never);
    dismiss();
  };

  const onInputKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSel((s) => Math.min(s + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSel((s) => Math.max(s - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      run(filtered[sel]);
    }
  };

  return (
    <Modal open={open} onClose={dismiss} title="Go to…">
      <div className="flex flex-col gap-2" data-testid="shell-command-palette">
        <input
          autoFocus
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onInputKey}
          placeholder="Search apps and pages…"
          aria-label="Command palette search"
          className="w-full rounded-md border border-[var(--color-border-default)] bg-[var(--color-surface-base)] px-3 py-2 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent)]"
        />
        <ul role="listbox" aria-label="Commands" className="max-h-80 overflow-y-auto">
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-sm text-[var(--color-text-secondary)]">No matches</li>
          ) : (
            filtered.map((c, i) => (
              <li
                key={c.id}
                role="option"
                aria-selected={i === sel}
                onMouseEnter={() => setSel(i)}
                onClick={() => run(c)}
                className={`flex cursor-pointer items-center justify-between rounded-[2px] px-3 py-2 text-sm text-[var(--color-text-primary)] ${
                  i === sel ? "bg-[var(--color-surface-muted)]" : ""
                }`}
              >
                <span>{c.label}</span>
                <span className="text-xs text-[var(--color-text-secondary)]">{c.group}</span>
              </li>
            ))
          )}
        </ul>
      </div>
    </Modal>
  );
}
